// Centralized Firestore service for all database operations
import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { db, isFirebaseReady } from '../firebase/config';

// Collection names
const COLLECTIONS = {
  PRODUCTS: 'products'
};

// Cache for products data
let productsCache = null;
let productsCacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Static data fallback (for build-time embedding)
const STATIC_PRODUCTS = [
  {
    id: 'Xo46mMqjN6glgRrMn5Mq',
    name: 'Fighter UAV',
    category: 'UAV',
    imageUrls: ['https://res.cloudinary.com/dz9qguszu/image/upload/v1703123456/centron-aerospace/products/hqnwkxws4vfhag56hdce.png'],
    imageUrl: 'https://res.cloudinary.com/dz9qguszu/image/upload/v1703123456/centron-aerospace/products/hqnwkxws4vfhag56hdce.png',
    specs: {
      'Weight': '2.5 kg',
      'Range': '10 km',
      'Battery': '45 min'
    },
    createdAt: '2023-12-21T10:30:56.000Z',
    updatedAt: '2023-12-21T10:30:56.000Z'
  },
  {
    id: 'Yp57nNrK7hmhSsNn6Nr',
    name: 'Surveillance Drone',
    category: 'UAV',
    imageUrls: ['https://res.cloudinary.com/dz9qguszu/image/upload/v1703123457/centron-aerospace/products/iqoxlxxt5wgibh67iefd.png'],
    imageUrl: 'https://res.cloudinary.com/dz9qguszu/image/upload/v1703123457/centron-aerospace/products/iqoxlxxt5wgibh67iefd.png',
    specs: {
      'Weight': '1.8 kg',
      'Range': '15 km',
      'Battery': '60 min'
    },
    createdAt: '2023-12-21T10:31:00.000Z',
    updatedAt: '2023-12-21T10:31:00.000Z'
  },
  {
    id: 'Zq68oSsL8inIiTtOo7Os',
    name: 'Combat System',
    category: 'Combat',
    imageUrls: ['https://res.cloudinary.com/dz9qguszu/image/upload/v1703123458/centron-aerospace/products/jrpylyyu6xhjci78jfge.png'],
    imageUrl: 'https://res.cloudinary.com/dz9qguszu/image/upload/v1703123458/centron-aerospace/products/jrpylyyu6xhjci78jfge.png',
    specs: {
      'Range': '25 km',
      'Accuracy': '99.9%',
      'Response Time': '< 1s'
    },
    createdAt: '2023-12-21T10:31:05.000Z',
    updatedAt: '2023-12-21T10:31:05.000Z'
  }
];

// Check if static mode is enabled
const isStaticMode = () => {
  return process.env.REACT_APP_USE_STATIC_DATA === 'true' || 
         (process.env.NODE_ENV === 'production' && process.env.REACT_APP_STATIC_MODE === 'true');
};

// Check if cache is valid
const isCacheValid = () => {
  if (!productsCache || !productsCacheTimestamp) return false;
  return Date.now() - productsCacheTimestamp < CACHE_DURATION;
};

// Wait for Firebase to be ready
const waitForFirebase = async (maxAttempts = 5, delay = 1000) => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (isFirebaseReady()) {
      console.log(`Firebase ready on attempt ${attempt}`);
      return true;
    }
    console.log(`Waiting for Firebase... Attempt ${attempt}/${maxAttempts}`);
    await new Promise(resolve => setTimeout(resolve, delay));
  }
  throw new Error('Firebase not ready after maximum attempts');
};

// Get products from Firestore
const getProductsFromFirestore = async (retryCount = 0) => {
  const maxRetries = process.env.NODE_ENV === 'production' ? 3 : 1;
  
  try {
    console.log(`getProductsFromFirestore: Starting... (attempt ${retryCount + 1}/${maxRetries + 1})`);
    
    // Wait for Firebase to be ready
    await waitForFirebase();
    
    if (!db) {
      throw new Error('Firebase database not initialized');
    }
    
    console.log('getProductsFromFirestore: Creating collection reference...');
    const productsCollection = collection(db, COLLECTIONS.PRODUCTS);
    
    console.log('getProductsFromFirestore: Executing query...');
    
    // Add timeout - longer for production
    const timeoutDuration = process.env.NODE_ENV === 'production' ? 15000 : 10000;
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error(`Query timeout after ${timeoutDuration/1000} seconds`)), timeoutDuration);
    });
    
    const queryPromise = getDocs(productsCollection);
    
    const querySnapshot = await Promise.race([queryPromise, timeoutPromise]);
    
    console.log('getProductsFromFirestore: Query completed, docs count:', querySnapshot.docs.length);
    
    const products = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log('getProductsFromFirestore: Products processed:', products.length);
    return products;
    
  } catch (error) {
    console.error(`getProductsFromFirestore: Error occurred (attempt ${retryCount + 1}):`, error);
    
    // Check for specific Firebase errors
    if (error.code === 'permission-denied') {
      throw new Error('Access denied. Please check your permissions.');
    } else if (error.code === 'unavailable') {
      throw new Error('Firebase service is currently unavailable. Please try again later.');
    } else if (error.message.includes('timeout')) {
      // Retry logic for timeouts
      if (retryCount < maxRetries) {
        console.log(`getProductsFromFirestore: Retrying... (${retryCount + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, 2000 * (retryCount + 1))); // Exponential backoff
        return getProductsFromFirestore(retryCount + 1);
      } else {
        throw new Error('Query failed after multiple attempts. Please check your connection and try again.');
      }
    }
    
    throw error;
  }
};

// Main function to get products with caching and static mode support
export const getProducts = async (forceRefresh = false) => {
  try {
    console.log('getProducts: Starting...');
    
    // Check if static mode is enabled
    if (isStaticMode()) {
      console.log('getProducts: Using static data mode');
      return STATIC_PRODUCTS;
    }
    
    // Check cache first (unless force refresh is requested)
    if (!forceRefresh && isCacheValid()) {
      console.log('getProducts: Returning cached data');
      return productsCache;
    }
    
    // Get fresh data from Firestore
    console.log('getProducts: Fetching fresh data from Firestore');
    const products = await getProductsFromFirestore();
    
    // Update cache
    productsCache = products;
    productsCacheTimestamp = Date.now();
    
    console.log('getProducts: Data cached successfully');
    return products;
    
  } catch (error) {
    console.error('getProducts: Error occurred:', error);
    
    // If Firestore fails and we have cached data, return it
    if (productsCache && !forceRefresh) {
      console.log('getProducts: Firestore failed, returning cached data');
      return productsCache;
    }
    
    // If no cache and static mode is available, use static data
    if (STATIC_PRODUCTS.length > 0) {
      console.log('getProducts: Using static data as fallback');
      return STATIC_PRODUCTS;
    }
    
    throw error;
  }
};

// Clear cache (useful for testing or manual refresh)
export const clearProductsCache = () => {
  productsCache = null;
  productsCacheTimestamp = null;
  console.log('Products cache cleared');
};

// Get cache status
export const getCacheStatus = () => {
  return {
    hasCache: !!productsCache,
    isCacheValid: isCacheValid(),
    cacheAge: productsCacheTimestamp ? Date.now() - productsCacheTimestamp : null,
    cacheDuration: CACHE_DURATION,
    staticMode: isStaticMode()
  };
};

// Add a new product
export const addProduct = async (productData) => {
  try {
    if (isStaticMode()) {
      throw new Error('Cannot add products in static mode');
    }
    
    await waitForFirebase();
    
    const docRef = await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
    console.log('Product added with ID:', docRef.id);
    
    // Clear cache to force refresh on next getProducts call
    clearProductsCache();
    
    return docRef;
  } catch (error) {
    console.error('Error adding product:', error);
    throw error;
  }
};

// Get a single product by ID
export const getProductById = async (productId) => {
  try {
    if (isStaticMode()) {
      const product = STATIC_PRODUCTS.find(p => p.id === productId);
      return product || null;
    }
    
    await waitForFirebase();
    
    const docRef = doc(db, COLLECTIONS.PRODUCTS, productId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.log('No such product!');
      return null;
    }
  } catch (error) {
    console.error('Error getting product:', error);
    throw error;
  }
};

// Update a product
export const updateProduct = async (productId, productData) => {
  try {
    if (isStaticMode()) {
      throw new Error('Cannot update products in static mode');
    }
    
    await waitForFirebase();
    
    const docRef = doc(db, COLLECTIONS.PRODUCTS, productId);
    const updateDataWithTimestamp = {
      ...productData,
      updatedAt: new Date()
    };
    await updateDoc(docRef, updateDataWithTimestamp);
    console.log('Product updated successfully');
    
    // Clear cache to force refresh on next getProducts call
    clearProductsCache();
  } catch (error) {
    console.error('Error updating product:', error);
    throw error;
  }
};

// Delete a product
export const deleteProduct = async (productId) => {
  try {
    if (isStaticMode()) {
      throw new Error('Cannot delete products in static mode');
    }
    
    await waitForFirebase();
    
    const docRef = doc(db, COLLECTIONS.PRODUCTS, productId);
    await deleteDoc(docRef);
    console.log('Product deleted successfully');
    
    // Clear cache to force refresh on next getProducts call
    clearProductsCache();
  } catch (error) {
    console.error('Error deleting product:', error);
    throw error;
  }
};

// Get products by category
export const getProductsByCategory = async (category) => {
  try {
    const products = await getProducts();
    return products.filter(product => product.category === category);
  } catch (error) {
    console.error('Error getting products by category:', error);
    throw error;
  }
};

// Search products by name
export const searchProductsByName = async (searchTerm) => {
  try {
    const products = await getProducts();
    const lowerSearchTerm = searchTerm.toLowerCase();
    return products.filter(product => 
      product.name.toLowerCase().includes(lowerSearchTerm)
    );
  } catch (error) {
    console.error('Error searching products:', error);
    throw error;
  }
};

// Get products with pagination
export const getProductsWithPagination = async (limitCount = 10, lastDoc = null) => {
  try {
    if (isStaticMode()) {
      const products = STATIC_PRODUCTS;
      const startIndex = lastDoc ? products.findIndex(p => p.id === lastDoc.id) + 1 : 0;
      const endIndex = startIndex + limitCount;
      const paginatedProducts = products.slice(startIndex, endIndex);
      
      return {
        products: paginatedProducts,
        lastDoc: paginatedProducts[paginatedProducts.length - 1] || null,
        hasMore: endIndex < products.length
      };
    }
    
    await waitForFirebase();
    
    let q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    if (lastDoc) {
      q = query(q, startAfter(lastDoc));
    }
    
    const querySnapshot = await getDocs(q);
    const products = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1];
    
    return {
      products,
      lastDoc: lastVisible,
      hasMore: querySnapshot.docs.length === limitCount
    };
  } catch (error) {
    console.error('Error getting products with pagination:', error);
    throw error;
  }
};

// Export constants for use in other files
export { COLLECTIONS, STATIC_PRODUCTS };
